import React, {
  useCallback, useMemo, useRef,
} from 'react';
import PropTypes from 'prop-types';
import { useDrag, useDrop } from 'react-dnd';
import { Box, ListItemButton } from '@mui/material';
import itemTypes from '../itemTypes';
import TableControls from '../../../BaseEditor/tableControls';
import EditorCheckbox from '../../../BaseEditor/EditorCheckbox';
import PluralValues from './pluralValues';

function FiltersItem({
  name, label, onClick, active, onDblClick, onHover, padded, errored,
  use, operation, value, allowedOperations, ctype, resource, isPlural,
  changeFilterUse, changeFilterOperation, changeFilterValue, showControl,
  choices, twoControls,
}) {
  const ref = useRef(null);
  const [{ isOver }, drop] = useDrop({
    accept: [itemTypes.availableField, itemTypes.filter],
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      isOverCurrent: monitor.isOver({ shallow: true }),
    }),
    hover: (item, monitor) => {
      if (ref.current) {
        const gbcr = ref.current.getBoundingClientRect();
        const cursor = monitor.getClientOffset();
        if ((gbcr.top + gbcr.height / 2) > cursor.y) {
          onHover(name);
        }
      }
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: itemTypes.filter,
    item: { name, type: itemTypes.filter },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const dragProps = useMemo(() => ({
    opacity: isDragging ? 0.4 : 1,
    color: errored ? 'error.main' : 'primary.main',
    width: 1,
  }), [errored, isDragging]);

  const btnProps = useMemo(() => ({
    pt: padded ? 3 : 0.5,
    transition: 'padding 0.3s ease-out',
    bgcolor: isOver ? 'grey.100' : null,
  }), [isOver, padded]);

  drop(ref);

  const onChanfeUseHandler = useCallback(
    (e, v) => changeFilterUse(name, v),
    [changeFilterUse, name],
  );

  const onChangeOperationHandler = useCallback(
    (e, v) => changeFilterOperation(name, v),
    [changeFilterOperation, name],
  );

  const onChangeValueHandler = useCallback(
    (e, v) => {
      if (!twoControls) return changeFilterValue(name, v);
      if (!value || !Array.isArray(value) || value.length < 2) return changeFilterValue(name, [v, null]);
      return changeFilterValue(name, [v, value[1]]);
    },
    [changeFilterValue, name, twoControls, value],
  );

  const onChangeValue2Handler = useCallback(
    (e, v) => {
      if (!twoControls) return null;
      if (!value || !Array.isArray(value) || value.length < 2) return changeFilterValue(name, [null, v]);
      return changeFilterValue(name, [value[0], v]);
    },
    [changeFilterValue, name, twoControls, value],
  );

  const useChoices = !!choices;

  const selectorValues = useMemo(
    () => {
      if (!useChoices) return null;
      return choices.map(([v, l]) => ({ value: v, display_name: l }));
    },
    [choices, useChoices],
  );
  const v1 = useMemo(
    () => {
      if (twoControls && !Array.isArray(value)) return null;
      if (twoControls && Array.isArray(value)) return value[0];
      return value;
    },
    [twoControls, value],
  );
  const v2 = useMemo(
    () => {
      if (!twoControls) return null;
      if (twoControls && (!Array.isArray(value) || value.length) < 2) return null;
      return value[1];
    },
    [twoControls, value],
  );
  return (
    <ListItemButton
      sx={btnProps}
      onClick={() => onClick(name)}
      selected={active}
      ref={ref}
      data-name={name}
    >
      <Box display="flex" alignItems="center" ref={drag} sx={dragProps}>
        <EditorCheckbox
          id={`filter_check_${name}`}
          label={label}
          value={use}
          onChange={onChanfeUseHandler}
          onDoubleClick={() => onDblClick(name)}
        />
        <TableControls.Selector
          id={`filter_operation_${name}`}
          value={operation}
          values={allowedOperations}
          onChange={onChangeOperationHandler}
        />
        {isPlural && showControl && (
          <PluralValues
            value={v1}
            onChange={onChangeValueHandler}
            ctype={ctype}
            resource={resource}
            short={!active}
          />
        )}
        {!isPlural && showControl && !useChoices && (
          <TableControls.MultiVaueInput
            id={`filter_value_${name}`}
            onChange={onChangeValueHandler}
            value={v1}
            ctype={ctype}
            resource={resource}
          />
        )}
        {!isPlural && showControl && useChoices && (
          <TableControls.Selector
            onChange={onChangeValueHandler}
            value={v1}
            values={selectorValues}
          />
        )}
        {twoControls && !useChoices && (
          <TableControls.MultiVaueInput
            id={`filter_value_${name}_2`}
            onChange={onChangeValue2Handler}
            value={v2}
            ctype={ctype}
            resource={resource}
          />
        )}
      </Box>
    </ListItemButton>
  );
}

FiltersItem.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  use: PropTypes.bool.isRequired,
  operation: PropTypes.string.isRequired,
  value: PropTypes.arrayOf(PropTypes.shape()),
  allowedOperations: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onClick: PropTypes.func.isRequired,
  onDblClick: PropTypes.func,
  active: PropTypes.bool,
  onHover: PropTypes.func.isRequired,
  padded: PropTypes.bool,
  changeFilterUse: PropTypes.func.isRequired,
  changeFilterOperation: PropTypes.func.isRequired,
  changeFilterValue: PropTypes.func.isRequired,
  errored: PropTypes.bool,
  ctype: PropTypes.string.isRequired,
  resource: PropTypes.string,
  isPlural: PropTypes.bool,
  showControl: PropTypes.bool,
  choices: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.string, PropTypes.bool, PropTypes.number,
  ]))),
  twoControls: PropTypes.bool,
};

FiltersItem.defaultProps = {
  label: '???',
  value: null,
  active: false,
  onDblClick: () => null,
  padded: false,
  errored: false,
  resource: null,
  isPlural: false,
  showControl: true,
  choices: null,
  twoControls: false,
};

export default FiltersItem;
